import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ClipboardCheck, Search } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { useActiveTenant } from '../contexts/tenant'

type Row = { productId: string; sku: string; name: string; saldo: number }

export function ContagemPage() {
  const tenantId = useActiveTenant()
  const [q, setQ] = useState('')
  const [counts, setCounts] = useState<Record<string, string>>({})
  const [zeroUncounted, setZeroUncounted] = useState(false)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)

  const { data: rows = [], isLoading, refetch } = useQuery<Row[]>({
    queryKey: ['count-sheet', tenantId],
    queryFn: async () => (await api.get('/stock/count-sheet', { params: { tenantId } })).data,
    enabled: !!tenantId,
  })

  if (!tenantId) {
    return <div className="flex h-64 items-center justify-center text-sm text-gray-500">Selecione um cliente no menu lateral para continuar.</div>
  }

  const term = q.trim().toLowerCase()
  const shown = term ? rows.filter(r => `${r.sku} ${r.name}`.toLowerCase().includes(term)) : rows
  const preenchidos = Object.values(counts).filter(v => v.trim() !== '').length

  async function aplicar() {
    const parsed = Object.entries(counts)
      .map(([productId, v]) => ({ productId, quantity: Number(v) }))
      .filter(c => c.quantity >= 0 && !Number.isNaN(c.quantity) && (counts[c.productId] ?? '').trim() !== '')
    if (!parsed.length) { setMsg('Digite ao menos uma contagem.'); return }
    const aviso = zeroUncounted
      ? `Aplicar contagem de ${parsed.length} SKU(s) e ZERAR todos os outros com saldo?`
      : `Aplicar contagem de ${parsed.length} SKU(s)?`
    if (!window.confirm(aviso)) return
    setBusy(true); setMsg(null)
    try {
      const { data: r } = await api.post('/stock/count', { tenantId, counts: parsed, zeroUncounted })
      setMsg(`Contagem aplicada: ${r.ajustados} SKU(s) ajustado(s).`)
      setCounts({})
      refetch()
    } catch (e: any) {
      setMsg(e?.response?.data?.title ?? 'Falha ao aplicar a contagem')
    } finally { setBusy(false) }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Contagem de inventário</h2>
          <p className="text-sm text-gray-500">Dia zero · digite o contado de cada SKU e aplique o saldo</p>
        </div>
        <Button loading={busy} disabled={preenchidos === 0} onClick={aplicar}>
          <ClipboardCheck className="h-4 w-4" /> Aplicar contagem ({preenchidos})
        </Button>
      </div>

      {msg && <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-800">{msg}</div>}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input
            className="w-full rounded-lg border border-gray-200 pl-9 pr-3 py-2 text-sm"
            placeholder="Buscar SKU ou produto..."
            value={q}
            onChange={e => setQ(e.target.value)}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" className="h-4 w-4" checked={zeroUncounted} onChange={e => setZeroUncounted(e.target.checked)} />
          Zerar os não contados
        </label>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Carregando {rows.length} produtos...</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">SKU</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Produto</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-600">Saldo atual</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-600">Contado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {shown.map(r => {
                    const val = counts[r.productId] ?? ''
                    const changed = val.trim() !== '' && Number(val) !== r.saldo
                    return (
                      <tr key={r.productId} className={changed ? 'bg-amber-50' : 'hover:bg-gray-50'}>
                        <td className="px-4 py-2 font-mono font-medium text-gray-800">{r.sku}</td>
                        <td className="px-4 py-2 text-gray-600">{r.name}</td>
                        <td className="px-4 py-2 text-right text-gray-500">{r.saldo}</td>
                        <td className="px-4 py-2 text-right">
                          <input
                            type="number"
                            min={0}
                            inputMode="numeric"
                            className="w-24 rounded border border-gray-300 px-2 py-1 text-right focus:border-blue-500 focus:outline-none"
                            value={val}
                            onChange={e => setCounts(c => ({ ...c, [r.productId]: e.target.value }))}
                          />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
